// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledBox from "@conklin/XDL/Components/Common/Box";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getDiscountTotal,
    getOrderPromotions,
    getPromotionsDataView,
    getShippingPromotions,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay, { CartTotalDisplayStyles } from "@insite/content-library/Components/CartTotalDisplay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    const promotions = getPromotionsDataView(state, state.pages.onePageCheckoutConfirmation.cartId).value;
    let orderPromotions;
    let shippingPromotions;
    let discountTotal;
    if (promotions) {
        orderPromotions = getOrderPromotions(promotions);
        shippingPromotions = getShippingPromotions(promotions);
        discountTotal = getDiscountTotal(promotions);
    }

    return {
        cart,
        promotions,
        orderPromotions,
        shippingPromotions,
        discountTotal,
        showTaxAndShipping: getSettingsCollection(state)?.cartSettings?.showTaxAndShipping,
    };
};
type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationOrderSummaryStyles {
    container?: InjectableCss;
    cartTotal?: CartTotalDisplayStyles;
    notesContainer?: InjectableCss;
    notesTitle?: TypographyPresentationProps;
    notesText?: TypographyPresentationProps;
    // XNG-Change
    messageText?: TypographyPresentationProps;
    divider?: InjectableCss;
}

export const orderSummaryStyles: OnePageCheckoutConfirmationOrderSummaryStyles = {
    container: {
        css: css`
            flex-direction: column;
            margin-top: 15px;
        `,
    },
    cartTotal: {
        container: {
            gap: 10,
            css: css`
                padding: 0;
                background-color: transparent;
            `,
        },
    },
    notesContainer: {
        css: css`
            flex-direction: column;
            margin-top: 15px;
        `,
    },
    notesTitle: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
};

const styles = orderSummaryStyles;

const OnePageCheckoutConfirmationOrderSummary = ({
    cart,
    promotions,
    orderPromotions,
    shippingPromotions,
    discountTotal,
    showTaxAndShipping,
}: Props) => {
    // XNG-Changes
    // if (!cart || !promotions) {
    //     return null;
    // }

    return (
        <StyledBox>
            <CartTotalDisplay
                cart={cart}
                orderPromotions={orderPromotions}
                shippingPromotions={shippingPromotions}
                discountTotal={discountTotal}
                // extendedStyles={styles.cartTotal}
                // XNG-Changes
                isLoading={!cart || !promotions}
                showTaxAndShipping={showTaxAndShipping}
            />
            {/* moved to OnePageCheckoutConfirmationBillingAndShipping */}
            {/* {cart.notes && (
                <StyledWrapper {...styles.notesContainer}>
                    <Typography {...styles.notesTitle}>{translate("Notes")}</Typography>
                    <Typography {...styles.notesText}>{cart.notes}</Typography>
                </StyledWrapper>
            )} */}
            {/* XNG-Change */}
            <StyledWrapper {...styles.divider} />
            <Typography {...styles.messageText}>{siteMessage("Custom_OrderSummary_ShippingAndHandling")}</Typography>
            <Typography {...styles.messageText}>{siteMessage("Custom_OrderSummary_MicroCopy")}</Typography>
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationOrderSummary),
    definition: {
        displayName: "Order Summary",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
